import { Component, Input, Output, EventEmitter } from '@angular/core';
import moment from 'moment';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'automata-datetime-picker',
  template: `
    <div clas="container">
      <div *ngIf="label" class="glxy-label">{{ label | translate }}</div>

      <div class="fields-container">
        <glxy-form-field style="font-size: 16px;" [class.invalid]="invalidDate | async" [required]="required">
          <input
            matInput
            [matDatepickerFilter]="!allowDatesInPast ? filter : null"
            [matDatepicker]="picker"
            value="{{ date.toISOString() }}"
            (change)="updateDateFromText($event.target.value)"
            (dateChange)="onDateChange($event)"
          />
          <mat-datepicker-toggle
            matSuffix
            [for]="picker"
            style="padding: 0; min-width: initial;"
          ></mat-datepicker-toggle>
          <mat-datepicker #picker [startAt]="date"></mat-datepicker>
        </glxy-form-field>
        <span class="time-picker-container">
          <mat-select [(ngModel)]="hour">
            <mat-option *ngFor="let i of hours" [value]="i">{{ ('0' + i).slice(-2) }}</mat-option>
          </mat-select>
          :
          <mat-select [(ngModel)]="minute">
            <mat-option *ngFor="let i of minutes" [value]="i">{{ ('0' + i).slice(-2) }}</mat-option>
          </mat-select>
          <mat-select [(ngModel)]="ampm" style="width: 45px;">
            <mat-option [value]="'am'">am</mat-option>
            <mat-option [value]="'pm'">pm</mat-option>
          </mat-select>
        </span>
      </div>
    </div>
  `,
  styleUrls: ['./datetime-picker.component.scss'],
  imports: [
    CommonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    GalaxyFormFieldModule,
    TranslateModule,
  ],
})
export class DatetimePickerComponent {
  @Input() datetime: Date;
  @Input() allowDatesInPast = true;
  @Output() datetimeChanged = new EventEmitter<Date>();
  @Output() invalidDate = new EventEmitter<boolean>(false);
  @Input() label: string;
  @Input() required = false;

  todayDate = new Date(Date.now()).setHours(0, 0, 0, 0);
  hours = Array(12)
    .fill(0)
    .map((x, i) => i + 1);
  minutes = Array(60)
    .fill(0)
    .map((x, i) => i);

  filter = (d: Date | null): boolean => {
    const date = d || new Date();
    return moment(date).isSameOrAfter(this.todayDate);
  };

  get date(): Date {
    return this.datetime;
  }

  set date(value: Date) {
    this.datetime.setFullYear(value.getFullYear(), value.getMonth(), value.getDate());
    this.datetimeChanged.emit(this.datetime);
    this.validateDate(this.datetime);
  }

  validateDate(date: Date | string): boolean {
    const valid = moment(date).isValid() && moment(date).isSameOrAfter(this.todayDate);
    this.invalidDate.emit(!valid);
    return valid;
  }

  updateDateFromText(value: string): void {
    if (this.validateDate(value)) {
      this.date = moment(value).toDate();
    }
  }

  onDateChange(event: MatDatepickerInputEvent<any>): void {
    if (moment.isMoment(event.value)) {
      this.date = event.value.toDate();
    } else if (moment.isDate(event.value)) {
      this.date = event.value;
    }
  }

  get hour(): number {
    const hours = this.datetime.getHours() % 12;
    return hours === 0 ? 12 : hours;
  }

  set hour(value: number) {
    this.datetime.setHours(this.ampm === 'am' ? value : value + 12);
    this.datetimeChanged.emit(this.datetime);
  }

  get minute(): number {
    return this.datetime.getMinutes();
  }

  set minute(value: number) {
    this.datetime.setMinutes(value);
    this.datetimeChanged.emit(this.datetime);
  }

  get ampm(): string {
    return this.datetime.getHours() < 12 ? 'am' : 'pm';
  }

  set ampm(value: string) {
    this.datetime.setHours(value === 'am' ? this.hour % 12 : this.hour < 12 ? this.hour + 12 : this.hour);
    this.datetimeChanged.emit(this.datetime);
  }
}
